import { useCallback, useMemo, useState } from 'react';

import { useLocale } from '@/hooks';
import { MIXED_DIFFICULTY_VALUE } from '@/shared/features/ai-quiz-generation/constants';
import {
  type AchievementStandardWithOrigin,
  type BuildPromptOptions,
} from '@/shared/features/ai-quiz-generation/types';

import { type AttachmentState } from './types';

/**
 * AI 퀴즈 생성 폼 상태 관리 훅
 *
 * useAiBuild의 상태 관리 책임을 분리하여 응집도를 높인 훅입니다.
 * attachment, textPrompt, options 등 폼 관련 상태를 통합 관리합니다.
 *
 * 주요 기능:
 * - Attachment 상태 관리 (file/youtube/none)
 * - Text prompt 입력 상태 관리
 * - 퀴즈 생성 옵션 관리 (언어, 타입, 난이도, 개수)
 * - 생성 가능 여부 계산 (attachment 또는 textPrompt 존재)
 *
 * @param params - selectedAchievementStandards 및 initialPrompt 파라미터
 * @example
 * ```typescript
 * const formState = useAiFormState({
 *   selectedAchievementStandards,
 *   initialPrompt: '초등학교 5학년 과학 문제'
 * });
 *
 * // Attachment 업데이트
 * formState.updateAttachment({ type: 'file', data: file });
 *
 * // Text prompt 업데이트
 * formState.updateTextPrompt('초등학교 5학년 과학 문제');
 *
 * // 생성 가능 여부 확인
 * if (formState.canGenerate) {
 *   // 퀴즈 생성 로직
 * }
 * ```
 */
export const useAiFormState = ({
  selectedAchievementStandards,
  initialPrompt,
  topicId,
}: {
  selectedAchievementStandards: AchievementStandardWithOrigin[];
  initialPrompt?: string;
  topicId?: string;
}) => {
  const { locale } = useLocale();

  // Attachment 상태 (Discriminated Union)
  const [attachmentState, setAttachmentState] = useState<AttachmentState>({
    type: 'none',
    data: null,
  });

  // Text prompt 상태
  const [textPrompt, setTextPrompt] = useState<string>(initialPrompt ?? '');

  // 퀴즈 생성 옵션 상태
  // 쿼터 제한 유저는 quizCount를 10으로 고정
  const [options, setOptions] = useState<BuildPromptOptions>({
    language: locale,
    quizType: 'mixed',
    // 기본값은 mixed 난이도 — quizType 'mixed' 와 짝을 맞춰 사용자가 한 화면
    // 안에서 다양한 난이도/유형의 결과를 받아볼 수 있게 한다.
    quizLevel: MIXED_DIFFICULTY_VALUE,
    quizCount: 10, // 쿼터 제한 유저는 항상 10, 로그인 유저도 기본값 10
  });

  /**
   * Attachment 상태 업데이트
   */
  const updateAttachment = useCallback((newState: AttachmentState) => {
    setAttachmentState(newState);
  }, []);

  /**
   * Text prompt 업데이트
   */
  const updateTextPrompt = useCallback((prompt: string) => {
    setTextPrompt(prompt);
  }, []);

  /**
   * 퀴즈 생성 옵션 업데이트
   */
  const updateOptions = useCallback((newOptions: BuildPromptOptions) => {
    setOptions(newOptions);
  }, []);

  /**
   * Attachment 여부 확인
   */
  const isAttached = attachmentState.type !== 'none';

  /**
   * 생성 가능 여부 계산
   * attachment가 있거나 textPrompt가 있거나 topicId가 있으면 생성 가능
   */
  const canGenerate = useMemo(() => {
    return isAttached || !!textPrompt || !!topicId;
  }, [isAttached, textPrompt, topicId]);

  return {
    // 상태
    attachmentState,
    textPrompt,
    topicId,
    options,
    isAttached,
    canGenerate,
    selectedAchievementStandards,

    // 업데이트 함수
    updateAttachment,
    updateTextPrompt,
    updateOptions,
  };
};
